import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useSubscriptionPlans } from "@/hooks/useSubscriptionPlans";
import {
  useAssignPlan,
  useAssignCustomSubscription,
} from "@/hooks/useSubscriptions";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  ownerId: string;
}

export const AssignPlanDialog = ({ open, onOpenChange, ownerId }: Props) => {
  const { data, isLoading } = useSubscriptionPlans();
  const assignStandard = useAssignPlan(ownerId);
  const assignCustom = useAssignCustomSubscription(ownerId);

  const [mode, setMode] = useState<"STANDARD" | "CUSTOM">("STANDARD");

  // Standard plan state
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [billingCycle, setBillingCycle] = useState<"MONTHLY" | "YEARLY">(
    "MONTHLY",
  );

  // Custom subscription state
  const [customAmount, setCustomAmount] = useState<string>("5000");
  const [customDays, setCustomDays] = useState<string>("90");
  const [customNotes, setCustomNotes] = useState<string>("");

  const [confirming, setConfirming] = useState(false);

  const plans = (data?.data ?? []).filter((p) => p.isActive);
  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  const isPending = assignStandard.isPending || assignCustom.isPending;

  const handleConfirm = () => {
    if (mode === "STANDARD") {
      if (!selectedPlanId) return;
      assignStandard.mutate(
        { ownerId, planId: selectedPlanId, billingCycle },
        {
          onSuccess: () => {
            toast.success("Subscription plan assigned successfully");
            handleClose();
          },
          onError: (e) => toast.error(e.message),
        },
      );
    } else {
      const amountNum = Number(customAmount);
      const daysNum = Number(customDays);
      if (isNaN(amountNum) || amountNum < 0) {
        toast.error("Please enter a valid subscription amount");
        return;
      }
      if (isNaN(daysNum) || daysNum <= 0) {
        toast.error("Please enter a valid duration in days");
        return;
      }

      assignCustom.mutate(
        {
          ownerId,
          amount: amountNum,
          durationDays: daysNum,
          notes: customNotes,
          paymentStatus: "PAID",
        },
        {
          onSuccess: () => {
            toast.success("Custom subscription created successfully");
            handleClose();
          },
          onError: (e) => toast.error(e.message),
        },
      );
    }
  };

  const handleClose = () => {
    if (isPending) return;
    onOpenChange(false);
    setConfirming(false);
    setSelectedPlanId("");
    setMode("STANDARD");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {confirming
              ? "Confirm Subscription Assignment"
              : "Assign Subscription"}
          </DialogTitle>
        </DialogHeader>

        {confirming ? (
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              This will replace any active subscription and start a new{" "}
              {mode === "STANDARD" ? (
                <span className="font-medium text-foreground">
                  {selectedPlan?.name} plan (
                  {billingCycle === "YEARLY" ? "Yearly" : "Monthly"})
                </span>
              ) : (
                <span className="font-medium text-foreground">
                  Custom Subscription of ₹
                  {Number(customAmount).toLocaleString()} for {customDays} days
                </span>
              )}
              .
            </p>
            <p className="text-sm font-medium">
              Are you sure you want to proceed?
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Mode Selector */}
            <div className="flex rounded-lg border border-border p-1 bg-muted/20">
              <button
                type="button"
                onClick={() => setMode("STANDARD")}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  mode === "STANDARD"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Standard Plan
              </button>
              <button
                type="button"
                onClick={() => setMode("CUSTOM")}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  mode === "CUSTOM"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Custom Subscription
              </button>
            </div>

            {mode === "STANDARD" ? (
              isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Select Plan
                    </label>
                    <div className="grid gap-2">
                      {plans.map((plan) => (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => setSelectedPlanId(plan.id)}
                          className={`w-full text-left rounded-card border p-3 text-sm transition-colors ${
                            selectedPlanId === plan.id
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-muted-foreground/40"
                          }`}
                        >
                          <div className="font-medium">{plan.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            ₹{Number(plan.priceMonthly).toLocaleString()}/mo
                            {plan.priceYearly
                              ? ` · ₹${Number(plan.priceYearly).toLocaleString()}/yr`
                              : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Billing cycle
                    </label>
                    <div className="flex gap-2">
                      {(["MONTHLY", "YEARLY"] as const).map((cycle) => (
                        <button
                          key={cycle}
                          type="button"
                          onClick={() => setBillingCycle(cycle)}
                          className={`flex-1 py-2 rounded-btn text-sm border transition-colors ${
                            billingCycle === cycle
                              ? "border-primary bg-primary/5 text-primary font-medium"
                              : "border-border hover:border-muted-foreground/40"
                          }`}
                        >
                          {cycle === "MONTHLY" ? "Monthly" : "Yearly"}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Custom Amount (₹)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g. 5000"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Duration in Days
                  </label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g. 30, 90, 180, 365"
                    value={customDays}
                    onChange={(e) => setCustomDays(e.target.value)}
                  />
                  <div className="flex gap-1 mt-1">
                    {[30, 90, 180, 365].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setCustomDays(String(d))}
                        className="px-2 py-0.5 text-xs rounded border border-border hover:bg-muted"
                      >
                        {d} days
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Notes / Description
                  </label>
                  <Input
                    placeholder="Reason or plan notes..."
                    value={customNotes}
                    onChange={(e) => setCustomNotes(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          {confirming ? (
            <Button onClick={handleConfirm} disabled={isPending}>
              {isPending ? "Saving…" : "Confirm"}
            </Button>
          ) : (
            <Button
              onClick={() => setConfirming(true)}
              disabled={
                mode === "STANDARD"
                  ? !selectedPlanId
                  : !customAmount || !customDays
              }
            >
              Next
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
